var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Gradients } from "apparatus/gradients";
import { P, Pivot, Segments, SharedColors } from "apparatus/library/common";
import { Color, Gradient, Group, Path, Segment, Shape } from "paper";
import { staticImplements } from "types";
import { metalRod, smoothCornersIndividual, strokeWidth } from "../draw";
import { lerp, R } from "./common";
const CLAMP_LENGTH = 180;
const clampSizeProp = {
    spec: "slider", key: "clampSize", label: "Clamp size",
    min: 30, max: 130
};
const clampColorProp = {
    spec: "radio", key: "clampColor", label: "Material",
    options: [
        { value: "metal", label: "Metal" },
        { value: "blue", label: "Blue rubber" },
    ]
};
/**
 * https://imgaz1.staticbg.com/thumb/large/oaupload/banggood/images/65/38/1992bd64-406b-476d-bb34-b6d63962c28d.JPG.webp
 */
let Clamp = class Clamp {
    constructor() {
        this.drivers = [
            {
                type: "rectangle",
                minX: 20, maxX: CLAMP_LENGTH + 20,
                minY: -20, maxY: -20,
                fromAppearance: a => P(a.shift * CLAMP_LENGTH + 20, -20),
                toAppearance: (f, p) => f({ shift: (p.x - 20) / CLAMP_LENGTH }),
                scale: 1.0,
            }
        ];
    }
    static convertToLatest(a) {
        if (!("type" in a)) {
            return Object.assign(Object.assign({}, a), { type: "simple" });
        }
        return a;
    }
    render(appearance) {
        let length = CLAMP_LENGTH;
        let handleThickness = 15;
        let handleWidth = appearance.clampSize;
        // Create the bolt.
        var bolt = createBolt();
        // Create the rod and the handler.
        let [rod, rodHighlight] = metalRod(P(0, 0), P(length, 0));
        const shiftable = new Group([rod, rodHighlight]);
        if (appearance.type === "3-prong") {
            // Back part
            // - Metal
            let backMetal_outline = new Path(Segments([[27, -9]], [[37, -4]], [[appearance.clampSize + 25, -4], , [7, 0]]))
                .mirrorY()
                .close()
                .withStroke("default")
                .withPositionDelta({ x: CLAMP_LENGTH });
            let backMetal_stem = new Path(Segments([[27, -9]], [[37, -4], , [10, 0]]))
                .mirrorY()
                .withGradientFill("right", [
                ["#666666", 0.20],
                ["#444444", 0.60],
            ])
                .close()
                .withPositionDelta({ x: CLAMP_LENGTH });
            let backMetal_prong = new Path(Segments([[37, -4]], [[appearance.clampSize + 25, -4], , [7, 0]]))
                .mirrorY()
                .close()
                .withGradientFill("right", [
                ["#444444", 0.40],
                ["#777777", 0.60],
            ])
                .withPositionDelta({ x: CLAMP_LENGTH });
            shiftable.addChildren([backMetal_stem, backMetal_prong, backMetal_outline]);
            // Front part
            // - Metal
            let frontMetal_outline = new Path(Segments([[0, -7]], [[21, -9]], [[35, -16], [-9, 0]], [[appearance.clampSize + 25, -16], , [7, 0]], [[appearance.clampSize + 25, -9], [7, 0]], [[35, -9], , [-9, 0]]))
                .mirrorY()
                .close()
                .withStroke("default")
                .withPositionDelta({ x: CLAMP_LENGTH });
            smoothCornersIndividual(frontMetal_outline, { 0: 3, 11: 3 });
            let frontMetal_stem = new Path(Segments([[0, -7]], [[21, -9]], [[35, -16], [-9, 0]], [[35, -9], , [-9, 0]]))
                .mirrorY()
                .close()
                .withGradientFill("right", [
                ["#aaaaaa", 0.55],
                ["#cccccc", 0.7],
            ])
                .withPositionDelta({ x: CLAMP_LENGTH });
            let frontMetal_prong1 = new Path(Segments([[35, -16], [-9, 0]], [[appearance.clampSize + 25, -16], , [7, 0]], [[appearance.clampSize + 25, -9], [7, 0]], [[35, -9], , [-9, 0]]))
                .close()
                .withGradientFill("right", [
                ["#cccccc", 0.40],
                ["#999999", 0.60],
            ])
                .withPositionDelta({ x: CLAMP_LENGTH });
            let frontMetal_prong2 = new Path(Segments([[35, 9], [-9, 0]], [[appearance.clampSize + 25, 9], , [7, 0]], [[appearance.clampSize + 25, 16], [7, 0]], [[35, 16], , [-9, 0]]))
                .close()
                .withGradientFill("right", [
                ["#cccccc", 0.40],
                ["#999999", 0.60],
            ])
                .withPositionDelta({ x: CLAMP_LENGTH });
            // Add metal highlights
            const highlight1 = new Path.Line(P(length + 32, 0), P(length + handleWidth + 28, 0))
                .withStroke(3.0, "#ffffff80");
            highlight1.strokeCap = "round";
            const highlight2 = highlight1.clone();
            highlight1.withPositionDelta({ y: -14 });
            highlight2.withPositionDelta({ y: 11 });
            shiftable.addChildren([frontMetal_stem, frontMetal_prong1, frontMetal_prong2, highlight1, highlight2, frontMetal_outline]);
            // - Soft pad
            if (appearance.clampColor !== "metal") {
                let pad1 = new Path.Rectangle(R(length + 40, 0, handleWidth - 15, 9, Pivot.CENTER_LEFT), 3)
                    .withGradientFill("right", [
                    ["#3c82c9", 0.40],
                    ["#33618f", 0.60],
                ])
                    .withStroke("thin", "#15385b");
                const pad2 = pad1.clone();
                const pad3 = pad1.clone()
                    .withGradientFill("right", [
                    ["#1a3a59", 0.40],
                    ["#25598c", 0.60],
                ])
                    .withStroke("thin", "#142a41");
                pad1.withPositionDelta({ y: -12.5 });
                pad2.withPositionDelta({ y: 12.5 });
                pad3.withPositionDelta({ x: 3 });
                shiftable.addChildren([pad1, pad2, pad3]);
            }
        }
        else if (appearance.type === "simple") {
            let handleR = R(length, -handleThickness / 2, handleWidth, handleThickness);
            let handle = new Path.Rectangle(handleR, 4.0);
            setHandleColor(handle, appearance.clampColor);
            let handleHighlight = new Path.Line(P(6, 0), P(handleWidth - 6, 0));
            handleHighlight.position.x += length;
            handleHighlight.position.y -= handleThickness / 2 - 3;
            handleHighlight.strokeWidth = 3.0;
            handleHighlight.strokeColor = "#ffffff40";
            handleHighlight.strokeCap = "round";
            shiftable.addChildren([handle, handleHighlight]);
        }
        else {
            // Should not happen.
        }
        // Move the rod.
        shiftable.position.x -= lerp(0.95, 0, appearance.shift) * length;
        // Assemble and compute hit shape.
        let graphic = new Group([shiftable, bolt]);
        let hitShape = Shape.Rectangle(graphic.bounds);
        return {
            graphic: graphic,
            hitShape: hitShape,
            pivot: P(0, 0),
            snapping: { type: "attachable", at: P(0, 0) }
        };
    }
};
Clamp.properties = {
    label: "Retort clamp",
    flippable: true,
    defaultAppearance: {
        type: "3-prong",
        shift: 0.8,
        clampSize: 55,
        clampColor: "blue",
    },
    appearanceSpecs: [
        {
            spec: "radio", key: "type", label: "Type",
            options: [
                { value: "3-prong", label: "3 prong", i18nLabel: "props.3prong" },
                { value: "simple", label: "Simple" },
            ],
        },
        {
            spec: "slider", key: "shift", label: "Shift",
            min: 0, max: 1, step: 0.01
        },
        clampSizeProp,
        clampColorProp,
    ],
};
Clamp = __decorate([
    staticImplements()
], Clamp);
export { Clamp };
export class FrontClamp {
    render(appearance) {
        const { clampSize, clampColor } = appearance;
        // Clamp.
        const back = Shape.Rectangle(R(0, 0, clampSize, 10, Pivot.CENTER), 5.0)
            .withGradientFill("right", Gradients.reflected(["#888888", 0.0], ["#666666", 0.25], ["#444444", 0.4]));
        const reflection = new Path.Line(P(-clampSize / 2 + 5, -2), P(clampSize / 2 - 5, -2))
            .withStroke(2.0, "#ffffff30");
        const left = Shape.Circle(P(-clampSize / 2, 0), 7.0);
        const right = Shape.Circle(P(clampSize / 2, 0), 7.0);
        setHandleColorForFront(left, clampColor);
        setHandleColorForFront(right, clampColor);
        // Bolt (side view).
        const bolt = Shape.Rectangle(R(-5, 0, 8, 20, Pivot.CENTER_RIGHT), 2.0);
        bolt.withStroke("thin", SharedColors.stroke)
            .withGradientFill("down", [["#888888", 0.0], ["#666666", 0.2], [SharedColors.darkMetal, 1.0]]);
        const graphic = new Group([bolt, back, reflection, left, right]);
        return {
            graphic: graphic,
            hitShape: graphic.bounds.expand(15).toShape(),
            pivot: P(0, 0),
            snapping: { type: "attachable", at: P(0, 0) }
        };
    }
}
FrontClamp.properties = {
    label: "Retort clamp (Front view)",
    flippable: true,
    defaultAppearance: {
        clampSize: 45,
        clampColor: "blue"
    },
    appearanceSpecs: [
        clampSizeProp,
        clampColorProp,
    ],
};
export function createBolt() {
    const steps = 7;
    const stepAngle = 360 / steps;
    var segmentA = new Segment(P(0, -10), P(-1, 2), P(2, -1));
    var segmentB = new Segment(P(0, -10).rotate(stepAngle / 2, P(0, 0)), P(-2, -1).rotate(stepAngle / 2), P(1, 2).rotate(stepAngle / 2));
    const bolt = new Path();
    for (var i = 0; i < steps; i++) {
        segmentA = segmentA.clone();
        rotateSegment(segmentA, stepAngle, P(0, 0));
        segmentB = segmentB.clone();
        rotateSegment(segmentB, stepAngle, P(0, 0));
        bolt.addSegments([segmentA, segmentB]);
    }
    bolt.closePath();
    bolt.withStroke("thin", SharedColors.stroke);
    bolt.setRadialGradientFill([
        ["#555555", 0.4],
        [SharedColors.darkMetal, 0.5],
        ["#666666", 0.6],
        [SharedColors.darkMetal, 0.7],
        ["#666666", 0.95],
    ], { center: P(0, 0) });
    return bolt;
}
function rotateSegment(segment, angle, pivot) {
    segment.point = segment.point.rotate(angle, pivot);
    if (segment.handleIn)
        segment.handleIn = segment.handleIn.rotate(angle);
    if (segment.handleOut)
        segment.handleOut = segment.handleOut.rotate(angle);
}
export class Ring {
    render(appearance) {
        let length = 180;
        let ringWidth = appearance.ringSize;
        let ringHeight = 5.0 + ringWidth * 0.15;
        // Create the bolt.
        var bolt = createBolt();
        // Create the rod.
        let [rod, rodHighlight] = metalRod(P(0, 0), P(length, 0));
        // Create the ring.
        let ring = Shape.Ellipse(R(length, -ringHeight / 2, ringWidth, ringHeight));
        ring.strokeWidth = 8.0;
        ring.strokeColor = SharedColors.darkMetal;
        // Create ring reflections.
        let reflectionSize = ringWidth * 0.3;
        let reflection1 = ringReflection(ring, 1, reflectionSize);
        let reflection2 = ringReflection(ring, -2.3, reflectionSize);
        reflection1.position.x += length;
        reflection2.position.x += length;
        // Ring group
        let ringGroup = new Group([ring, reflection1, reflection2]);
        if (appearance.hole) {
            // Add a mask for the back of the ring.
            let ringCenter = length + ringWidth / 2;
            let holeWidth = Math.min(appearance.ringSize - 10, appearance.holeWidth);
            ringGroup.insertChild(0, new Path(Segments(
            // Start top right corner
            [[length + ringWidth + 5, -ringHeight]], [[length + ringWidth + 5, ringHeight]], [[length - 5, ringHeight]], [[length - 5, -ringHeight]], 
            // Actual hole.
            [[ringCenter - holeWidth / 2, -ringHeight]], [[ringCenter - holeWidth / 2, 0]], [[ringCenter + holeWidth / 2, 0]], [[ringCenter + holeWidth / 2, -ringHeight]])).close().withFill("red"));
            ringGroup.clipped = true;
        }
        // Move the rod.
        let shiftable = new Group([rod, rodHighlight, ringGroup]);
        shiftable.position.x -= lerp(0.95, 0, appearance.shift) * length;
        // Assemble and compute hit shape.
        let graphic = new Group([shiftable, bolt]);
        return {
            graphic: graphic,
            hitShape: graphic.strokeBounds.toShape(),
            pivot: P(0, 0),
            snapping: { type: "attachable", at: P(0, 0) }
        };
    }
}
Ring.properties = {
    label: "Retort Ring",
    flippable: true,
    defaultAppearance: {
        shift: 0.8,
        ringSize: 77,
        hole: false,
        holeWidth: 120
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "shift", label: "Shift",
            min: 0, max: 1, step: 0.01
        },
        {
            spec: "slider", key: "ringSize", label: "Clamp size",
            min: 35, max: 130
        },
        { spec: "header", header: "Mask", icon: "mask", toggleKey: "hole" },
        {
            spec: "slider", key: "holeWidth", label: "Size",
            min: 4, max: 120, visible: a => a.hole
        },
    ],
};
function ringReflection(ring, angle, width) {
    let ringWidth = ring.bounds.width;
    let ringHeight = ring.bounds.height;
    let reflectionGradient = new Gradient([["#ffffff60", 0.0], ["#ffffff00", 1.0]], true);
    let reflection = Shape.Ellipse(R(0, 0, width, 8));
    reflection.fillColor = new Color(reflectionGradient, P(width / 2, 4), P(width, 8));
    reflection.position.x = ringWidth / 2 + Math.cos(angle) * ringWidth / 2;
    reflection.position.y = Math.sin(angle) * ringHeight / 2;
    return reflection;
}
function setHandleColor(handle, color) {
    let gradient;
    switch (color) {
        case "blue":
            handle.strokeColor = "#26527f";
            gradient = new Gradient([
                ["#3c82c9", 0.1],
                ["#56a0ea", 0.3],
                ["#478cd1", 0.7],
                ["#3c82c9", 0.9],
            ]);
            break;
        case "metal":
        default:
            handle.strokeColor = SharedColors.darkMetal;
            gradient = new Gradient([
                ["#777777", 0.1],
                ["#aaaaaa", 0.22],
                ["#888888", 0.7],
                ["#444444", 1.0],
            ]);
    }
    handle.strokeWidth = strokeWidth("thin");
    handle.fillColor = new Color(gradient, handle.bounds.leftCenter, handle.bounds.rightCenter);
}
function setHandleColorForFront(item, color) {
    let stroke;
    let fill;
    switch (color) {
        case "blue":
            stroke = "#26527f";
            fill = "#3c82c9";
            break;
        case "metal":
            stroke = SharedColors.darkMetal;
            fill = "#888888";
            break;
    }
    item.withFill(fill);
    item.withStroke("thin", stroke);
}
